import { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { IS_TENANT, BUSINESS_NAME } from '../tenant/config';

// Parcel Audit — look up parcel/express scans (from the Supabase parcel_events table, was
// Sheet1) to settle count disputes. Each row shows its AP Signoff ID + workcentre + status so
// a "101 vs 100" claim is traceable per parcel.
//
// Two scopes:
//  • Driver — one driver's individual scans (detailed table + Depot Bags/DoE aux cards).
//  • All Drivers / City / Darlinghurst / Broadway — every driver in that area aggregated into a
//    per-driver summary (Parcels + EP split out) with grand totals; each row expands to its scans.
const API = '/.netlify/functions/parcel-audit-sb';
const DEPOT_API = '/.netlify/functions/depot-sb';
const DOE_API = '/.netlify/functions/doe-sb';

// Scope tabs. 'driver' keeps the single-driver combobox; the rest run an area aggregate.
// City / Darlinghurst / Broadway are Hollypaul's areas; a tenant build gets Driver + All Drivers.
const SCOPES = [
  { key: 'driver', label: 'Driver' },
  { key: 'all', label: 'All Drivers', area: 'ALL' },
  ...(IS_TENANT ? [] : [
    { key: 'City', label: 'City', area: 'City' },
    { key: 'Darlinghurst', label: 'Darlinghurst', area: 'Darlinghurst' },
    { key: 'Broadway', label: 'Broadway', area: 'Broadway' },
  ]),
];
// Brand on the printed PDF. Tested on the raw env var (a build-time constant) rather than
// IS_TENANT so the minifier drops our name from a tenant's bundle altogether.
const BRAND = process.env.REACT_APP_MODULES ? BUSINESS_NAME.toUpperCase() : 'HOLLYPAUL';

const cell = { padding: '7px 10px', borderBottom: '1px solid var(--hp-border)', fontSize: '0.85rem', textAlign: 'left', whiteSpace: 'nowrap' };
const th = { ...cell, color: 'var(--hp-dim)', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: '0.04em' };
const field = { padding: '8px 10px', borderRadius: 8, border: '1px solid var(--hp-border)', background: 'var(--hp-bg)', color: 'var(--hp-text)', fontSize: '0.9rem' };
const stat = { flex: 1, minWidth: 120, padding: '12px 14px', borderRadius: 10, border: '1px solid var(--hp-border)', background: 'var(--hp-surface)' };

// Keep an ISO row inside the (optional) from/to window. ISO yyyy-mm-dd sorts lexically.
const inRange = (iso, from, to) => (!from || iso >= from) && (!to || iso <= to);

// What happened to the scanner audit (scan_events) when the parcel rows moved: the days
// whose detail followed, the days not uploaded yet (corrected on arrival), and the rare
// case where the correction couldn't be recorded at all.
function scanAuditNote(sa, from, to) {
  if (!sa) return ' The scanner audit was left under the old login.';
  const bits = [];
  if (sa.moved) bits.push(`Scanner audit: ${sa.moved.toLocaleString()} scan line${sa.moved === 1 ? '' : 's'} across ${sa.days.length} day${sa.days.length === 1 ? '' : 's'} moved too (${sa.days.map(d => d.date).join(', ')}).`);
  if (sa.pending && sa.pending.length) {
    const days = sa.pending.join(', ');
    bits.push(sa.recorded
      ? `${days} isn't uploaded yet — it'll be corrected automatically when that day's audit file goes in.`
      : `${days} isn't uploaded yet, and the correction could NOT be saved — move it by hand on the Scanner Audit page once that day is loaded.`);
  }
  if (sa.empty && sa.empty.length) bits.push(`${sa.empty.join(', ')} is loaded but has no scan lines under ${from}, so there was nothing to move.`);
  if (sa.errors && sa.errors.length) bits.push(`Scanner audit problem: ${sa.errors.join('; ')}`);
  if (!bits.length) return ` No scanner audit lines were loaded for those days under ${from}, so nothing else to move to ${to}.`;
  return ' ' + bits.join(' ');
}

// One stat card (label + value). aux = dashed border for figures not from the parcel scans.
function StatCard({ label, value, aux, title }) {
  return (
    <div style={{ ...stat, ...(aux ? { borderStyle: 'dashed' } : null) }} title={title}>
      <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: '1.5rem', fontWeight: 600 }}>{value}</div>
    </div>
  );
}

// viewOnly — the driver portal's Parcel Audit tab (portal cookie, e.g. Valeria):
// lookups + XLSX/PDF downloads only. Hides Reassign (admin-gated server-side anyway)
// and the Depot Bags/DoE aux cards, whose feeds need a site login and would show blanks.
// A tenant build has no Depots/DoE feeds and no scanner audit, so it drops the aux cards
// and the "move the scanner audit too" option as well.
export default function ParcelAuditPage({ viewOnly = false }) {
  const noAux = viewOnly || IS_TENANT;
  const [scope, setScope] = useState('driver');
  // ?driver= deep link from the global search palette pre-fills the driver box
  // (prefill only — the user still presses Run).
  const [searchParams] = useSearchParams();
  const driverParam = (searchParams.get('driver') || '').toUpperCase();
  const [driver, setDriver] = useState(driverParam);
  useEffect(() => { if (driverParam) setDriver(driverParam); }, [driverParam]);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);       // single-driver result
  const [areaResult, setAreaResult] = useState(null); // area aggregate result
  const [error, setError] = useState('');

  // Driver autocomplete
  const [drivers, setDrivers] = useState([]);
  const [showList, setShowList] = useState(false);
  const blurTimer = useRef(null);

  // Extra (visual-only) totals from the Depots + DoE feeds — single-driver scope only
  const [extras, setExtras] = useState(null); // { depotBags, doeCount }

  // Download Selected (single-driver scope)
  const [selected, setSelected] = useState(() => new Set());
  const [fmt, setFmt] = useState('xlsx'); // 'xlsx' | 'pdf'

  // Reassign Selected (single-driver scope) — move scans that landed on the wrong login
  // (a driver going out on someone else's scanner) to the driver who actually delivered them.
  const [reassignOpen, setReassignOpen] = useState(false);
  const [target, setTarget] = useState('');
  const [reassigning, setReassigning] = useState(false);
  // The scanner audit (Case Lookup / Driver Day / Downtime) follows the money by default —
  // same driver, same days — so the two stores never disagree about who did the day.
  const [moveScanAudit, setMoveScanAudit] = useState(true);
  const [notice, setNotice] = useState('');

  // Expanded driver rows (area scope)
  const [expanded, setExpanded] = useState(() => new Set());

  // Populate the combobox once. roster-drivers is the canonical login+name list; the input
  // stays free-text so any login not in the list can still be typed in full.
  // A tenant reads its own drivers table through parcel-audit-sb (roster-drivers isn't deployed there).
  useEffect(() => {
    const req = IS_TENANT
      ? fetch(API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'listDrivers' }) })
          .then(r => r.json()).then(d => (d && d.success ? d.drivers : []))
      : fetch('/.netlify/functions/roster-drivers').then(r => r.json());
    req
      .then(d => setDrivers(Array.isArray(d) ? d.filter(x => x && x.login) : []))
      .catch(() => {});
  }, []);

  const matches = useMemo(() => {
    const q = driver.trim().toUpperCase();
    const list = q
      ? drivers.filter(d => d.login.toUpperCase().includes(q) || (d.name || '').toUpperCase().includes(q))
      : drivers;
    return list.slice(0, 8);
  }, [driver, drivers]);

  function switchScope(next) {
    if (next === scope) return;
    setScope(next);
    setError(''); setResult(null); setAreaResult(null);
    setSelected(new Set()); setExpanded(new Set()); setExtras(null);
    setReassignOpen(false); setTarget(''); setNotice('');
  }

  async function lookup(e) {
    if (e) e.preventDefault();
    if (scope === 'driver') return lookupDriver();
    return lookupArea();
  }

  async function lookupDriver() {
    const dr = driver.trim();
    if (!dr) { setError('Enter a driver login'); return; }
    setShowList(false);
    setLoading(true); setError(''); setResult(null); setAreaResult(null); setExtras(null); setSelected(new Set());
    setReassignOpen(false); setNotice('');
    try {
      const res = await fetch(API, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'getParcelAudit', driver: dr, from, to }),
      });
      const d = await res.json();
      if (!d.success) throw new Error(d.error || 'Lookup failed');
      setResult(d);
      if (!noAux) loadExtras(dr);   // fire-and-forget; the scan table doesn't wait on the visual cards
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  }

  async function lookupArea() {
    const sc = SCOPES.find(s => s.key === scope);
    setLoading(true); setError(''); setResult(null); setAreaResult(null); setExpanded(new Set());
    try {
      const res = await fetch(API, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'getAreaAudit', area: sc ? sc.area : 'ALL', from, to }),
      });
      const d = await res.json();
      if (!d.success) throw new Error(d.error || 'Lookup failed');
      setAreaResult(d);
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  }

  // Depot Bags + DoE totals for the same driver/range. Both feeds filter by pay period, not a
  // date range, so we pull the driver's rows and window them client-side by from/to. Best-effort:
  // a failure here just leaves the cards blank — it must never block the parcel audit.
  async function loadExtras(dr) {
    const post = (url, body) => fetch(url, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    }).then(r => r.json()).catch(() => ({ success: false }));
    try {
      const [dep, doe] = await Promise.all([
        post(DEPOT_API, { action: 'getDepotBagsTotals', driver: dr }),
        post(DOE_API, { action: 'getDoEConfirmations', driver: dr }),
      ]);
      const depotBags = dep && dep.success
        ? (dep.rows || []).filter(r => inRange(r.date, from, to)).reduce((a, r) => a + (parseInt(r.tally, 10) || 0), 0)
        : null;
      const doeCount = doe && doe.success
        ? (doe.rows || []).filter(r => inRange(r.date, from, to)).length
        : null;
      setExtras({ depotBags, doeCount });
    } catch { setExtras(null); }
  }

  const rows = result ? result.rows : [];
  const allSelected = rows.length > 0 && selected.size === rows.length;

  const toggleRow = (i) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(i)) next.delete(i); else next.add(i);
    return next;
  });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(rows.map((_, i) => i)));
  const toggleExpand = (login) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(login)) next.delete(login); else next.add(login);
    return next;
  });

  function selectedRows() {
    return rows.filter((_, i) => selected.has(i));
  }

  const rangeLabel = () => (from || to ? `${from || 'start'}_to_${to || 'end'}` : '');

  // Move the selected scans to another driver's login. The server re-checks every row still
  // belongs to the on-screen driver, stamps the original login into the row, and the lookup
  // re-runs so the moved rows drop out of this driver's view immediately.
  async function doReassign() {
    const chosen = selectedRows();
    const toDriver = target.trim().toUpperCase();
    if (!chosen.length || !toDriver || reassigning) return;
    if (toDriver === result.driver) { setError('Target driver is the same login'); return; }
    const units = chosen.reduce((a, r) => a + (parseInt(r.total, 10) || 0), 0);
    setReassigning(true); setError(''); setNotice('');
    try {
      const res = await fetch(API, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'reassignScans', fromDriver: result.driver, toDriver, ids: chosen.map(r => r.id), moveScanAudit: moveScanAudit && !IS_TENANT }),
      });
      const d = await res.json();
      if (!d.success) throw new Error(d.error || 'Reassign failed');
      const fromDriver = result.driver;
      setReassignOpen(false); setTarget('');
      await lookupDriver();  // refresh — clears notice, so set it after
      setNotice(`Moved ${d.updated} scan${d.updated === 1 ? '' : 's'} (${units} units) from ${fromDriver} to ${toDriver}. Look up ${toDriver} to see them on their side.${scanAuditNote(d.scanAudit, fromDriver, toDriver)}`);
    } catch (err) { setError(err.message); } finally { setReassigning(false); }
  }

  function downloadSelected() {
    const chosen = selectedRows();
    if (!chosen.length) return;
    const label = `${result.driver}${from || to ? `_${rangeLabel()}` : ''}`;
    if (fmt === 'xlsx') exportXlsx(chosen, label);
    else exportPdf(chosen, label);
  }

  function downloadArea() {
    if (!areaResult) return;
    const label = `${areaResult.area.replace(/\s+/g, '-')}${from || to ? `_${rangeLabel()}` : ''}`;
    if (fmt === 'xlsx') exportAreaXlsx(areaResult, label);
    else exportAreaPdf(areaResult, label);
  }

  function triggerDownload(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  function exportXlsx(chosen, label) {
    const header = ['Date', 'Time', 'Type', 'Total', 'AP Signoff ID', 'Workcentre', 'Status'];
    const aoa = [header, ...chosen.map(r => [r.date, r.time, r.parcelType, r.total, r.apSignoffId, r.workcentre, r.status])];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [{ wch: 12 }, { wch: 10 }, { wch: 9 }, { wch: 8 }, { wch: 20 }, { wch: 14 }, { wch: 12 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Parcel Audit');
    const out = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
    triggerDownload(new Blob([out], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `parcel-audit_${label}.xlsx`);
  }

  // Area export — a Summary sheet (Parcels/EP split with a totals row) + an All Scans sheet
  // (every driver's rows, so the aggregate stays traceable back to individual signoff IDs).
  function exportAreaXlsx(ar, label) {
    const wb = XLSX.utils.book_new();
    const sHeader = ['Driver', 'Name', 'Area', 'Parcels', 'EP', 'Total', 'Scans'];
    const sAoa = [sHeader, ...ar.drivers.map(d => [d.login, d.name, d.area, d.parcel, d.express, d.total, d.count])];
    sAoa.push(['TOTAL', '', '', ar.totals.parcel, ar.totals.express, ar.totals.sum, ar.totals.count]);
    const sWs = XLSX.utils.aoa_to_sheet(sAoa);
    sWs['!cols'] = [{ wch: 14 }, { wch: 22 }, { wch: 12 }, { wch: 9 }, { wch: 9 }, { wch: 9 }, { wch: 8 }];
    XLSX.utils.book_append_sheet(wb, sWs, 'Summary');

    const dHeader = ['Driver', 'Date', 'Time', 'Type', 'Total', 'AP Signoff ID', 'Workcentre', 'Status'];
    const dAoa = [dHeader];
    ar.drivers.forEach(d => d.rows.forEach(r => dAoa.push([d.login, r.date, r.time, r.parcelType, r.total, r.apSignoffId, r.workcentre, r.status])));
    const dWs = XLSX.utils.aoa_to_sheet(dAoa);
    dWs['!cols'] = [{ wch: 14 }, { wch: 12 }, { wch: 10 }, { wch: 9 }, { wch: 8 }, { wch: 20 }, { wch: 14 }, { wch: 12 }];
    XLSX.utils.book_append_sheet(wb, dWs, 'All Scans');

    const out = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
    triggerDownload(new Blob([out], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `parcel-audit_${label}.xlsx`);
  }

  // PDF via a clean white print window (same pattern as the depot QR posters) — the user picks
  // "Save as PDF" in the print dialog. Avoids bundling a PDF renderer for a simple table.
  function exportPdf(chosen, label) {
    const esc = (s) => String(s == null ? '' : s).replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
    const totalUnits = chosen.reduce((a, r) => a + (parseInt(r.total, 10) || 0), 0);
    const rowsHtml = chosen.map(r => `<tr>
      <td>${esc(r.date)}</td><td>${esc(r.time)}</td><td>${esc(r.parcelType)}</td>
      <td class="num">${esc(r.total)}</td><td class="mono">${esc(r.apSignoffId)}</td>
      <td>${esc(r.workcentre)}</td><td>${esc(r.status)}</td>
    </tr>`).join('');
    const range = from || to ? `${from || '…'} → ${to || '…'}` : 'All dates';
    const generated = new Date().toLocaleString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8" />
<title>Parcel Audit — ${esc(result.driver)}</title>
${printCss()}</head><body>
  <div class="head">
    <div>
      <div class="title">PARCEL AUDIT — ${esc(result.driver)}</div>
      <div class="sub">${esc(range)} &nbsp;·&nbsp; ${chosen.length} scan${chosen.length === 1 ? '' : 's'} selected</div>
    </div>
    <div class="logo">${BRAND}</div>
  </div>
  <div class="cards">
    <div class="card"><div class="k">Scans</div><div class="v">${result.totals.count}</div></div>
    <div class="card"><div class="k">Parcels</div><div class="v">${result.totals.parcel}</div></div>
    <div class="card"><div class="k">EP</div><div class="v">${result.totals.express}</div></div>
    <div class="card"><div class="k">Total units</div><div class="v">${result.totals.sum}</div></div>
    ${noAux ? '' : `<div class="card aux"><div class="k">Depot Bags</div><div class="v">${extras && extras.depotBags != null ? extras.depotBags : '—'}</div></div>
    <div class="card aux"><div class="k">DoE</div><div class="v">${extras && extras.doeCount != null ? extras.doeCount : '—'}</div></div>`}
  </div>
  <table>
    <thead><tr><th>Date</th><th>Time</th><th>Type</th><th>Total</th><th>AP Signoff ID</th><th>Workcentre</th><th>Status</th></tr></thead>
    <tbody>${rowsHtml}</tbody>
    <tfoot><tr><td colspan="3">TOTAL UNITS</td><td class="num">${totalUnits}</td><td colspan="3"></td></tr></tfoot>
  </table>
  ${printFooter('Parcel Audit', generated)}
  <script>window.onload = function(){ window.print(); };</script>
</body></html>`;
    openPrintWindow(html);
  }

  // Area PDF — per-driver summary table (Parcels/EP split) with a grand-totals footer row.
  function exportAreaPdf(ar, label) {
    const esc = (s) => String(s == null ? '' : s).replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
    const rowsHtml = ar.drivers.map(d => `<tr>
      <td class="mono">${esc(d.login)}</td><td>${esc(d.name)}</td><td>${esc(d.area)}</td>
      <td class="num">${esc(d.parcel)}</td><td class="num">${esc(d.express)}</td>
      <td class="num">${esc(d.total)}</td><td class="num">${esc(d.count)}</td>
    </tr>`).join('');
    const range = from || to ? `${from || '…'} → ${to || '…'}` : 'All dates';
    const generated = new Date().toLocaleString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8" />
<title>Parcel Audit — ${esc(ar.area)}</title>
${printCss()}</head><body>
  <div class="head">
    <div>
      <div class="title">PARCEL AUDIT — ${esc(ar.area)}</div>
      <div class="sub">${esc(range)} &nbsp;·&nbsp; ${ar.totals.drivers} driver${ar.totals.drivers === 1 ? '' : 's'}</div>
    </div>
    <div class="logo">${BRAND}</div>
  </div>
  <div class="cards">
    <div class="card"><div class="k">Drivers</div><div class="v">${ar.totals.drivers}</div></div>
    <div class="card"><div class="k">Parcels</div><div class="v">${ar.totals.parcel}</div></div>
    <div class="card"><div class="k">EP</div><div class="v">${ar.totals.express}</div></div>
    <div class="card"><div class="k">Total units</div><div class="v">${ar.totals.sum}</div></div>
    <div class="card"><div class="k">Scans</div><div class="v">${ar.totals.count}</div></div>
  </div>
  <table>
    <thead><tr><th>Driver</th><th>Name</th><th>Area</th><th>Parcels</th><th>EP</th><th>Total</th><th>Scans</th></tr></thead>
    <tbody>${rowsHtml}</tbody>
    <tfoot><tr><td colspan="3">TOTAL</td><td class="num">${ar.totals.parcel}</td><td class="num">${ar.totals.express}</td><td class="num">${ar.totals.sum}</td><td class="num">${ar.totals.count}</td></tr></tfoot>
  </table>
  ${printFooter('Parcel Audit', generated)}
  <script>window.onload = function(){ window.print(); };</script>
</body></html>`;
    openPrintWindow(html);
  }

  const areaDrivers = areaResult ? areaResult.drivers : [];

  return (
    <div>
      <div className="page-header">
        <div className="page-header-left">
          <h2>Parcel Audit</h2>
          <p style={{ color: 'var(--hp-dim)', fontSize: '0.85rem', margin: '4px 0 0' }}>
            Look up parcel scans to settle count disputes — each row is traceable by its AP&nbsp;Signoff&nbsp;ID.
            Pick a single driver, or an area to compare everyone in it.
          </p>
        </div>
      </div>

      {/* Scope tabs — Driver vs area aggregates */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '4px 0 14px' }}>
        {SCOPES.map(s => (
          <button
            key={s.key}
            type="button"
            onClick={() => switchScope(s.key)}
            style={{
              padding: '7px 14px', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600,
              border: '1px solid var(--hp-border)',
              background: scope === s.key ? 'var(--hp-accent, #2a78d6)' : 'var(--hp-bg)',
              color: scope === s.key ? '#fff' : 'var(--hp-dim)',
            }}
          >
            {s.label}
          </button>
        ))}
      </div>

      <form onSubmit={lookup} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', margin: '4px 0 20px' }}>
        {scope === 'driver' && (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.78rem', color: 'var(--hp-dim)', position: 'relative' }}>
            Driver
            <input
              style={{ ...field, width: 200 }}
              value={driver}
              onChange={e => { setDriver(e.target.value); setShowList(true); }}
              onFocus={() => setShowList(true)}
              onBlur={() => { blurTimer.current = setTimeout(() => setShowList(false), 150); }}
              placeholder="Type a login or name…"
              autoComplete="off"
              autoFocus
            />
            {showList && matches.length > 0 && (
              <div
                onMouseDown={() => clearTimeout(blurTimer.current)}
                style={{ position: 'absolute', top: '100%', left: 0, zIndex: 20, marginTop: 4, width: 240, maxHeight: 260, overflowY: 'auto', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 8, boxShadow: '0 6px 20px rgba(0,0,0,0.18)' }}
              >
                {matches.map(d => (
                  <div
                    key={d.login}
                    onClick={() => { setDriver(d.login); setShowList(false); }}
                    style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--hp-border)', display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'var(--hp-bg)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    <span style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--hp-text)' }}>{d.login}</span>
                    <span style={{ fontSize: '0.75rem', color: 'var(--hp-dim)', textAlign: 'right' }}>{d.name}</span>
                  </div>
                ))}
              </div>
            )}
          </label>
        )}
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.78rem', color: 'var(--hp-dim)' }}>
          From (optional)
          <input type="date" style={field} value={from} onChange={e => setFrom(e.target.value)} />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.78rem', color: 'var(--hp-dim)' }}>
          To (optional)
          <input type="date" style={field} value={to} onChange={e => setTo(e.target.value)} />
        </label>
        <button type="submit" disabled={loading} style={{ ...field, cursor: 'pointer', background: 'var(--hp-accent, #2a78d6)', color: '#fff', border: 'none', fontWeight: 600, minWidth: 100 }}>
          {loading ? 'Looking up…' : 'Look up'}
        </button>
      </form>

      {error && <div style={{ color: '#c0392b', fontSize: '0.85rem', marginBottom: 16 }}>{error}</div>}
      {notice && <div style={{ color: '#1e7e34', fontSize: '0.85rem', marginBottom: 16, fontWeight: 600 }}>{notice}</div>}

      {/* ── Single-driver result ─────────────────────────────────────────── */}
      {scope === 'driver' && result && (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
            <StatCard label="Scans" value={result.totals.count} />
            <StatCard label="Parcels" value={result.totals.parcel} />
            <StatCard label="EP" value={result.totals.express} />
            <StatCard label="Total units" value={result.totals.sum} />
            {!noAux && <StatCard label="Depot Bags" aux title="From the Depots feed — not part of the parcel scans" value={extras ? (extras.depotBags == null ? '—' : extras.depotBags) : '…'} />}
            {!noAux && <StatCard label="DoE" aux title="From the DoE feed — not part of the parcel scans" value={extras ? (extras.doeCount == null ? '—' : extras.doeCount) : '…'} />}
          </div>

          {rows.length === 0 ? (
            <div style={{ color: 'var(--hp-dim)', fontSize: '0.9rem' }}>No scans found for {result.driver}{from || to ? ' in that range' : ''}.</div>
          ) : (
            <>
              {/* Download toolbar */}
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
                <span style={{ fontSize: '0.8rem', color: 'var(--hp-dim)' }}>{selected.size} selected</span>
                <FmtToggle fmt={fmt} setFmt={setFmt} />
                <button
                  type="button"
                  onClick={downloadSelected}
                  disabled={selected.size === 0}
                  style={{ ...field, cursor: selected.size === 0 ? 'not-allowed' : 'pointer', background: selected.size === 0 ? 'var(--hp-bg)' : 'var(--hp-gold, #c9a84c)', color: selected.size === 0 ? 'var(--hp-dim)' : 'var(--hp-black, #111)', border: 'none', fontWeight: 700 }}
                >
                  Download Selected
                </button>
                {!viewOnly && (
                  <button
                    type="button"
                    onClick={() => { setReassignOpen(o => !o); setError(''); setNotice(''); }}
                    disabled={selected.size === 0}
                    title="Move the selected scans to another driver — for when a driver went out on someone else's scanner login"
                    style={{ ...field, cursor: selected.size === 0 ? 'not-allowed' : 'pointer', background: 'var(--hp-bg)', color: selected.size === 0 ? 'var(--hp-dim)' : 'var(--hp-text)', fontWeight: 700 }}
                  >
                    ↷ Reassign Selected…
                  </button>
                )}
              </div>

              {/* Reassign panel — pick the driver who actually delivered the selected scans */}
              {!viewOnly && reassignOpen && selected.size > 0 && (
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 12, padding: '12px 14px', border: '1px dashed var(--hp-border)', borderRadius: 10, background: 'var(--hp-surface)' }}>
                  <div style={{ fontSize: '0.85rem', color: 'var(--hp-text)', alignSelf: 'center' }}>
                    Move <b>{selected.size} scan{selected.size === 1 ? '' : 's'}</b> ({selectedRows().reduce((a, r) => a + (parseInt(r.total, 10) || 0), 0)} units)
                    from <b>{result.driver}</b> to
                  </div>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.78rem', color: 'var(--hp-dim)' }}>
                    Driver who actually delivered them
                    <input
                      style={{ ...field, width: 200 }}
                      value={target}
                      onChange={e => setTarget(e.target.value.toUpperCase())}
                      placeholder="e.g. MEDINAHEM2"
                      list="reassign-driver-list"
                      autoComplete="off"
                    />
                    <datalist id="reassign-driver-list">
                      {drivers.map(d => <option key={d.login} value={d.login}>{d.name}</option>)}
                    </datalist>
                  </label>
                  <button
                    type="button"
                    onClick={doReassign}
                    disabled={reassigning || !target.trim() || target.trim().toUpperCase() === result.driver}
                    style={{ ...field, cursor: reassigning ? 'wait' : 'pointer', background: 'var(--hp-accent, #2a78d6)', color: '#fff', border: 'none', fontWeight: 700 }}
                  >
                    {reassigning ? 'Moving…' : `Move to ${target.trim().toUpperCase() || '…'}`}
                  </button>
                  <button type="button" onClick={() => { setReassignOpen(false); setTarget(''); }} style={{ ...field, cursor: 'pointer', background: 'var(--hp-bg)', color: 'var(--hp-dim)' }}>
                    Cancel
                  </button>
                  {target.trim() && !drivers.some(d => d.login.toUpperCase() === target.trim().toUpperCase()) && (
                    <div style={{ flexBasis: '100%', fontSize: '0.78rem', color: '#c0392b' }}>
                      {target.trim().toUpperCase()} isn't in the driver list — check the spelling before moving.
                    </div>
                  )}
                  {!IS_TENANT && <label style={{ flexBasis: '100%', display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: '0.8rem', color: 'var(--hp-text)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={moveScanAudit} onChange={e => setMoveScanAudit(e.target.checked)} style={{ marginTop: 3 }} />
                    <span>
                      Move the <b>scanner audit</b> for these days too
                      <span style={{ color: 'var(--hp-dim)' }}> — the scan-by-scan detail behind Case Lookup, Driver Day,
                      Downtime and Route Sequence. Leave this on unless the scans really were {result.driver}'s and only the
                      count was wrong. Days not uploaded yet are corrected when that day's audit file goes in.</span>
                    </span>
                  </label>}
                  <div style={{ flexBasis: '100%', fontSize: '0.75rem', color: 'var(--hp-dim)' }}>
                    This changes who these parcels are counted (and paid) under — RCTI quantities, activity reports and
                    team totals all follow. Each moved scan keeps a "was {result.driver}" marker so it stays traceable.
                  </div>
                </div>
              )}

              <div style={{ overflowX: 'auto', border: '1px solid var(--hp-border)', borderRadius: 10 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--hp-surface)' }}>
                  <thead>
                    <tr>
                      <th style={{ ...th, width: 34, textAlign: 'center' }}>
                        <input type="checkbox" checked={allSelected} onChange={toggleAll} aria-label="Select all" style={{ cursor: 'pointer' }} />
                      </th>
                      <th style={th}>Date</th><th style={th}>Time</th><th style={th}>Type</th>
                      <th style={{ ...th, textAlign: 'right' }}>Total</th><th style={th}>AP Signoff ID</th>
                      <th style={th}>Workcentre</th><th style={th}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i} style={selected.has(i) ? { background: 'rgba(42,120,214,0.08)' } : undefined}>
                        <td style={{ ...cell, textAlign: 'center' }}>
                          <input type="checkbox" checked={selected.has(i)} onChange={() => toggleRow(i)} aria-label={`Select row ${i + 1}`} style={{ cursor: 'pointer' }} />
                        </td>
                        <td style={cell}>{r.date}</td>
                        <td style={cell}>{r.time}</td>
                        <td style={cell}>{r.parcelType}</td>
                        <td style={{ ...cell, textAlign: 'right', fontWeight: 600 }}>{r.total}</td>
                        <td style={{ ...cell, fontFamily: 'var(--font-mono, monospace)', fontSize: '0.78rem' }}>{r.apSignoffId}</td>
                        <td style={cell}>{r.workcentre}</td>
                        <td style={cell}>
                          {r.status}
                          {r.reassignedFrom && (
                            <span title={`Reassigned — originally scanned as ${r.reassignedFrom}`} style={{ marginLeft: 8, fontSize: '0.72rem', color: 'var(--hp-dim)' }}>
                              ↷ was {r.reassignedFrom}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: 'var(--hp-bg)', fontWeight: 700 }}>
                      <td style={cell} />
                      <td style={cell} colSpan={2}>TOTAL</td>
                      <td style={cell}>{result.totals.parcel} P · {result.totals.express} EP</td>
                      <td style={{ ...cell, textAlign: 'right' }}>{result.totals.sum}</td>
                      <td style={cell} colSpan={3} />
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          )}
        </>
      )}

      {/* ── Area result ──────────────────────────────────────────────────── */}
      {scope !== 'driver' && areaResult && (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
            <StatCard label="Drivers" value={areaResult.totals.drivers} />
            <StatCard label="Parcels" value={areaResult.totals.parcel} />
            <StatCard label="EP" value={areaResult.totals.express} />
            <StatCard label="Total units" value={areaResult.totals.sum} />
            <StatCard label="Scans" value={areaResult.totals.count} />
          </div>

          {areaDrivers.length === 0 ? (
            <div style={{ color: 'var(--hp-dim)', fontSize: '0.9rem' }}>No scans found for {areaResult.area}{from || to ? ' in that range' : ''}.</div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
                <span style={{ fontSize: '0.8rem', color: 'var(--hp-dim)' }}>{areaResult.area} · {areaDrivers.length} driver{areaDrivers.length === 1 ? '' : 's'}</span>
                <FmtToggle fmt={fmt} setFmt={setFmt} />
                <button
                  type="button"
                  onClick={downloadArea}
                  style={{ ...field, cursor: 'pointer', background: 'var(--hp-gold, #c9a84c)', color: 'var(--hp-black, #111)', border: 'none', fontWeight: 700 }}
                >
                  Download
                </button>
                <span style={{ fontSize: '0.75rem', color: 'var(--hp-dim)' }}>Click a driver to see their scans</span>
              </div>

              <div style={{ overflowX: 'auto', border: '1px solid var(--hp-border)', borderRadius: 10 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--hp-surface)' }}>
                  <thead>
                    <tr>
                      <th style={{ ...th, width: 28 }} />
                      <th style={th}>Driver</th><th style={th}>Name</th><th style={th}>Area</th>
                      <th style={{ ...th, textAlign: 'right' }}>Parcels</th>
                      <th style={{ ...th, textAlign: 'right' }}>EP</th>
                      <th style={{ ...th, textAlign: 'right' }}>Total</th>
                      <th style={{ ...th, textAlign: 'right' }}>Scans</th>
                    </tr>
                  </thead>
                  <tbody>
                    {areaDrivers.map((d) => {
                      const open = expanded.has(d.login);
                      return (
                        <FragmentRow key={d.login} d={d} open={open} onToggle={() => toggleExpand(d.login)} />
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: 'var(--hp-bg)', fontWeight: 700 }}>
                      <td style={cell} />
                      <td style={cell} colSpan={3}>TOTAL · {areaResult.totals.drivers} driver{areaResult.totals.drivers === 1 ? '' : 's'}</td>
                      <td style={{ ...cell, textAlign: 'right' }}>{areaResult.totals.parcel}</td>
                      <td style={{ ...cell, textAlign: 'right' }}>{areaResult.totals.express}</td>
                      <td style={{ ...cell, textAlign: 'right' }}>{areaResult.totals.sum}</td>
                      <td style={{ ...cell, textAlign: 'right' }}>{areaResult.totals.count}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

// Format toggle (XLSX / PDF) shared by both scopes.
function FmtToggle({ fmt, setFmt }) {
  return (
    <div style={{ display: 'flex', border: '1px solid var(--hp-border)', borderRadius: 8, overflow: 'hidden' }}>
      {['xlsx', 'pdf'].map(f => (
        <button
          key={f}
          type="button"
          onClick={() => setFmt(f)}
          style={{ padding: '7px 14px', border: 'none', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', background: fmt === f ? 'var(--hp-accent, #2a78d6)' : 'var(--hp-bg)', color: fmt === f ? '#fff' : 'var(--hp-dim)' }}
        >
          {f}
        </button>
      ))}
    </div>
  );
}

// A driver summary row + (when expanded) an inline scan table for that driver.
function FragmentRow({ d, open, onToggle }) {
  return (
    <>
      <tr onClick={onToggle} style={{ cursor: 'pointer', background: open ? 'rgba(42,120,214,0.06)' : undefined }}>
        <td style={{ ...cell, textAlign: 'center', color: 'var(--hp-dim)' }}>{open ? '▾' : '▸'}</td>
        <td style={{ ...cell, fontWeight: 600 }}>{d.login}</td>
        <td style={cell}>{d.name}</td>
        <td style={cell}>{d.area}</td>
        <td style={{ ...cell, textAlign: 'right' }}>{d.parcel}</td>
        <td style={{ ...cell, textAlign: 'right' }}>{d.express}</td>
        <td style={{ ...cell, textAlign: 'right', fontWeight: 700 }}>{d.total}</td>
        <td style={{ ...cell, textAlign: 'right' }}>{d.count}</td>
      </tr>
      {open && (
        <tr>
          <td style={{ padding: 0, background: 'var(--hp-bg)' }} colSpan={8}>
            <div style={{ padding: '4px 10px 12px 38px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={th}>Date</th><th style={th}>Time</th><th style={th}>Type</th>
                    <th style={{ ...th, textAlign: 'right' }}>Total</th><th style={th}>AP Signoff ID</th>
                    <th style={th}>Workcentre</th><th style={th}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {d.rows.map((r, i) => (
                    <tr key={i}>
                      <td style={cell}>{r.date}</td>
                      <td style={cell}>{r.time}</td>
                      <td style={cell}>{r.parcelType}</td>
                      <td style={{ ...cell, textAlign: 'right', fontWeight: 600 }}>{r.total}</td>
                      <td style={{ ...cell, fontFamily: 'var(--font-mono, monospace)', fontSize: '0.78rem' }}>{r.apSignoffId}</td>
                      <td style={cell}>{r.workcentre}</td>
                      <td style={cell}>{r.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ── Shared print-window helpers (single + area PDFs use the same chrome) ──────
function printCss() {
  return `<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  @page { margin: 14mm 14mm 20mm; }
  body { font-family: -apple-system, Arial, sans-serif; color: #111; padding: 28px; }
  .head { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 2px solid #111; padding-bottom: 12px; margin-bottom: 16px; }
  .title { font-size: 1.3rem; font-weight: 800; letter-spacing: 0.04em; }
  .sub { font-size: 0.8rem; color: #555; margin-top: 4px; }
  .logo { font-size: 0.9rem; font-weight: 800; letter-spacing: 0.12em; }
  .cards { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 16px; }
  .card { flex: 1; min-width: 90px; border: 1px solid #ccc; border-radius: 6px; padding: 8px 10px; }
  .card.aux { border-style: dashed; }
  .card .k { font-size: 0.58rem; color: #777; text-transform: uppercase; letter-spacing: 0.04em; }
  .card .v { font-size: 1.2rem; font-weight: 800; margin-top: 2px; }
  table { width: 100%; border-collapse: collapse; }
  th, td { padding: 6px 9px; border: 1px solid #ccc; font-size: 0.78rem; text-align: left; }
  th { background: #111; color: #fff; text-transform: uppercase; font-size: 0.68rem; letter-spacing: 0.04em; }
  thead { display: table-header-group; }
  td.num { text-align: right; font-weight: 700; }
  td.mono { font-family: ui-monospace, Menlo, monospace; font-size: 0.72rem; }
  tfoot td { font-weight: 800; border-top: 2px solid #111; }
  .foot { position: fixed; bottom: 6mm; left: 0; right: 0; display: flex; justify-content: space-between;
          font-size: 0.62rem; color: #777; border-top: 1px solid #ddd; padding-top: 4px; }
  .foot .pageno::after { content: counter(page) " / " counter(pages); }
  @media print { body { padding: 0; } }
</style>`;
}

function printFooter(label, generated) {
  return `<div class="foot">
    <span>${BRAND} · ${label} — confidential</span>
    <span>Generated ${generated}</span>
    <span class="pageno">Page </span>
  </div>`;
}

function openPrintWindow(html) {
  const w = window.open('', '_blank', 'width=900,height=1000');
  if (w) { w.document.write(html); w.document.close(); }
}
